import React, { useState, forwardRef, useEffect, useMemo, useCallback } from 'react'

import styles from './styles.module.scss'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as SocMediaIcon } from './icons/tg&ns_icon.svg'
import { ReactComponent as LinkIcons } from '../../../../../assets/icons/link_icons.svg'
import { ReactComponent as GiftIcon } from './icons/gift_icon.svg'
import { ReactComponent as ConfettiIcon } from './icons/confetti_icon.svg'
import { ReactComponent as CopiedIcon } from '../../../../../assets/icons/copied_icon.svg'
import Button from '../../../../../UI/Button/Button'
import {
  referralActivate,
  referralExtend,
  setPromoCode,
  setReferralStart,
  testRefDelete,
} from '../../../../../api/postApi'
import { getDateByUserFormat } from '../../../../../helpers/timeHelper'
import Spinner from '../../../../../UI/Spinner/Spinner'
import { setReferralData } from '../../../../../redux/billing/billing.action'
import { testRefChangeBonus } from '../../../../../api/putApi'
import Input from '../../../../../UI/Input/Input'
import useInput from '../../../../../hooks/useInput'
import { getPromoCodeAbility } from '../../../../../api/getApi'
import UseReferral from '../../../../../hooks/useReferral'
import { notify } from '../../../../../helpers/notify'
import ShareBlock from '../../../../ShareBlock/ShareBlock'

const ReferralModal = forwardRef(({ promoCode }, ref) => {
  const [wasCopied, setWasCopied] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPromoCodeInput, setIsPromoCodeInput] = useState(false)
  const [isPromoCodeAvailable, setIsPromoCodeAvailable] = useState(false)
  const [isActivated, setActivated] = useState(null)
  const [createdLink, setCreatedLink] = useState(null)
  const [startPoints, setStartPoints] = useState(0)
  const [lowestPrise, setLowestPrise] = useState(50)
  const [isShareBlockOpen, setIsShareBlockOpen] = useState(false)

  const { value: referralCodeVal, onChange: handleSetPromoCode, setValue } = useInput('')

  const dispatch = useDispatch()

  const referralData = useSelector(({ billing }) => billing.referralData)

  const { fetchData, isLoading: isLoadingRef } = UseReferral()

  const {
    ns_member_count,
    ns_member_price,
    tg_member_count,
    tg_member_price,
    total_points,
    referral_link,
    products_price,
  } = referralData
  useMemo(() => {
    if (!Array.isArray(products_price) || products_price.length === 0) return

    const minPrice = Math.min(
      ...products_price
        .filter((item) => typeof item.product_price === 'number') // Фильтруем невалидные объекты
        .map((item) => item.product_price)
    )

    setLowestPrise(minPrice)
  }, [products_price])

  useEffect(() => {
    const fetchData = async () => {
      await getPromoCodeAbility()
        .then((res) => {
          setIsPromoCodeAvailable(false)
        })
        .catch((err) => {
          setIsPromoCodeAvailable(true)
        })
    }
    fetchData()

    setLowestPrise()

    if (promoCode) {
      setValue(promoCode)
    }
  }, [])

  const handleCopyLink = async () => {
    referral_link || createdLink
      ? await (async () => {
          setWasCopied(true)
          await navigator.clipboard.writeText(referral_link || createdLink)
          setTimeout(() => setWasCopied(false), 2000)
        })()
      : await (async () => {
          const { referral_link } = await setReferralStart()
          setCreatedLink(referral_link)
          setWasCopied(true)
          await navigator.clipboard.writeText(referral_link || createdLink)
          setTimeout(() => setWasCopied(false), 2000)
        })()
  }

  const handleActivateProduct = async (bonus_id) => {
    setIsLoading(true)
    const data = await referralActivate(bonus_id)
    dispatch(setReferralData(data))
    setActivated(products_price.find((item) => item.bonus_id === bonus_id))
    setIsLoading(false)
  }

  const handleExtendProduct = async (bonus_id) => {
    setIsLoading(true)
    const data = await referralExtend(bonus_id)
    dispatch(setReferralData(data))
    setActivated(false)
    setIsLoading(false)
  }

  const handleAddPromoCode = (e) => {
    e.stopPropagation()
    setIsPromoCodeInput(true)
  }

  const removeReferralParam = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('referral')
    window.history.replaceState(null, '', url.toString())
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const trimmedReferralCode = referralCodeVal.trim()
      await setPromoCode(trimmedReferralCode)
      notify('Success', 'Congratulations! Promo code added successfully.')
      localStorage.removeItem('referral')
      removeReferralParam()
      await fetchData()
      setIsPromoCodeAvailable(false)
      setValue('')
    } catch ({ error }) {
      if (error?.message) {
        notify.error('Error', error.message)
      }
    }
  }

  const handleShareBlockClose = (e) => {
    e.stopPropagation()
    setIsShareBlockOpen(false)
  }

  const handleShareBlockOpen = async (e) => {
    e.stopPropagation()

    if (!referral_link) {
      const result = await setReferralStart()
      const { referral_link } = result
      setCreatedLink(referral_link)
      setIsShareBlockOpen(true)
    }
    if (referral_link) {
      setIsShareBlockOpen(true)
    }
  }

  return (
    <div ref={ref} className={cn(styles.container, { [styles.container_big]: !isLoadingRef })}>
      {(window.location.href.includes('stage') ||
        window.location.href.includes('dev') ||
        window.location.href.includes('test') ||
        window.location.href.includes('localhost')) && (
        <div className={styles.test}>
          <button
            onClick={async () => {
              await testRefDelete()
            }}
          >
            delete referral
          </button>
          <br />
          <br />
          <div>
            <input value={startPoints} onChange={(e) => setStartPoints(e.target.value)} />
            <button
              onClick={async () => {
                await testRefChangeBonus(startPoints)
              }}
            >
              set {startPoints} bonuses
            </button>
          </div>
        </div>
      )}
      <div className={styles.leftSection}>
        {isPromoCodeAvailable && (
          <div className={styles.addCodeBlock}>
            <h3>Enter a Promo Code</h3>
            <p>
              If you registered using a referral link, the promo code was sent to you along with it. Enter your promo
              code to activate additional bonuses and enhance your experience on the platform.
            </p>
            {isPromoCodeInput || referralCodeVal ? (
              <form onSubmit={(e) => handleSubmit(e)} className={styles.actionBlock}>
                <Input placeholder={'Promo code '} onChange={handleSetPromoCode} value={referralCodeVal} />
                <Button theme={'white'} size={'middle-full'} type={'submit'}>
                  Apply
                </Button>
              </form>
            ) : (
              <Button theme={'bordered'} size={'middle-full'} onClick={(e) => handleAddPromoCode(e)}>
                Add promo code
              </Button>
            )}
          </div>
        )}
        <div className={styles.referralsBlock}>
          <b>My Referrals </b>
          <div className={styles.referralsData}>
            <div className={styles.referralsDataBlock}>
              NextShip {ns_member_count > 1 || ns_member_count === 0 ? 'users' : 'user'} <b>{ns_member_count || 0}</b>
            </div>
            <div className={styles.referralsDataBlock}>
              Telegram {tg_member_count > 1 || tg_member_count === 0 ? 'members' : 'member'}{' '}
              <b>{tg_member_count || 0}</b>
            </div>
            <div className={styles.referralsDataBlock}>
              {total_points > 1 || total_points === 0 ? 'Points' : 'Point'}{' '}
              <b
                className={cn(styles.userPoints, {
                  [styles.userPoints_active]: !isLoadingRef && total_points >= lowestPrise,
                  [styles.userPointsUnActive]: !isLoadingRef && total_points < lowestPrise,
                })}
              >
                {total_points || 0}
              </b>
            </div>
          </div>
        </div>
        <div className={styles.earningsBlock}>
          <div>
            <b>Earning Points: </b>
            <ul>
              <li>
                • For each <strong>new member</strong> joining the <strong>Telegram channel</strong> through your
                referral link, you will earn{' '}
                <span>
                  {tg_member_price} {tg_member_price > 1 ? 'points' : 'point'}{' '}
                </span>
              </li>
              <li>
                • For each <strong>new user</strong> registered on the <strong>NextShip platform</strong> through your
                referral link, you will earn{' '}
                <span>
                  {ns_member_price} {ns_member_price > 1 ? 'points' : 'point'}{' '}
                </span>
              </li>
            </ul>
          </div>
          <div>
            <SocMediaIcon />
          </div>
        </div>
        <div className={styles.usingBlock}>
          <b> Using Points:</b>
          {products_price?.map(({ product_icon, product_name, product_price, product_description, day_count }, i) => (
            <div key={i} className={styles.product}>
              <div>
                <img src={product_icon.file_mini} height={26} alt={product_name} />
              </div>
              <p>
                <strong>
                  Collect {product_price} {product_price > 1 ? 'points' : 'point'}{' '}
                </strong>{' '}
                to activate <strong>{product_name}</strong> for {day_count} days
              </p>
            </div>
          ))}
        </div>
        <div className={styles.linkBlock}>
          <button
            className={cn(styles.link_button, [wasCopied && styles.link_button_success_copied])}
            onClick={handleCopyLink}
          >
            {wasCopied ? (
              <>
                Link successfully copied <CopiedIcon />
              </>
            ) : (
              <>
                Copy referral link
                {!referral_link && !createdLink && <LinkIcons />}
              </>
            )}
          </button>
          <Button theme={'bordered'} size={'small'} onClick={handleShareBlockOpen} className={styles.shareButton}>
            Share
          </Button>
        </div>
        <div className={cn(styles.shareBlockWrapper, { [styles.shareBlockWrapper_open]: isShareBlockOpen })}>
          <ShareBlock
            defaultLink={referral_link || createdLink}
            onClose={handleShareBlockClose}
            isOpen={isShareBlockOpen}
          />
        </div>
      </div>
      {(total_points >= lowestPrise || products_price?.find(({ activate_status }) => !!activate_status)) && (
        <div className={styles.rightSection}>
          <div className={styles.congratulations}>
            <div className={styles.iconsBlock}>
              <GiftIcon className={styles.giftIcon} />
              <ConfettiIcon />
            </div>
            <b>
              <b>
                {products_price.some(({ activate_status }) => activate_status)
                  ? products_price
                      .filter(({ activate_status }) => activate_status)
                      .map(({ product_name }, index, arr) => {
                        if (index === arr.length - 1 && arr.length > 1) {
                          return ` and ${product_name}`
                        } else if (index > 0) {
                          return `, ${product_name}`
                        } else {
                          return '🎉 ' + product_name
                        }
                      })
                      .join('') + ' Activated! 🎉'
                  : '🎉 Congratulations! 🎉'}
              </b>
            </b>
            {isLoading ? (
              <div className={styles.spinnerBlock}>
                <Spinner width={150} height={150} />
              </div>
            ) : isActivated || products_price.find(({ activate_status }) => activate_status) ? (
              <div>Keep sharing your referral links with friends to extend your current active services!</div>
            ) : (
              <div className={styles.congratulationsBlock}>
                <p>
                  You've earned{' '}
                  <b>
                    {total_points} {total_points === 0 || total_points > 1 ? 'points' : 'point'}
                  </b>{' '}
                  to activate a service!
                </p>
                <div>
                  <p>👉 Choose one of the following:</p>
                  <ol>
                    {products_price.map(({ product_name, day_count }, i) => (
                      <li key={i}>
                        <strong>{product_name}</strong> – for {day_count} days
                      </li>
                    ))}
                  </ol>
                  <p>Click the button below to activate!</p>
                </div>
              </div>
            )}
            <div className={styles.actionBlock}>
              {products_price.map(
                (
                  {
                    product_name,
                    product_price,
                    product_description,
                    activate_status,
                    activate_expire,
                    buy_expire,
                    allow_activate,
                    allow_extend,
                    bonus_id,
                  },
                  i
                ) => (
                  <div key={i} className={styles.product}>
                    {allow_activate ? (
                      <Button
                        theme={'dark'}
                        size={'middle-full'}
                        className={styles.button}
                        onClick={() => handleActivateProduct(bonus_id)}
                      >
                        Active {product_name}
                      </Button>
                    ) : allow_extend ? (
                      <div className={styles.extend}>
                        <b>Valid until {getDateByUserFormat(new Date(activate_expire || buy_expire))}</b>
                        <Button
                          theme={'dark'}
                          size={'middle-full'}
                          className={styles.button}
                          onClick={() => handleExtendProduct(bonus_id)}
                        >
                          Extend {product_name}
                        </Button>
                      </div>
                    ) : activate_status ? (
                      <div className={styles.product_valid}>
                        Valid until {getDateByUserFormat(new Date(activate_expire || buy_expire))}
                      </div>
                    ) : (
                      <Button theme={'dark'} size={'middle-full'} disabled className={styles.button_disabled}>
                        Active {product_name}
                      </Button>
                    )}
                  </div>
                )
              )}
            </div>{' '}
            <div></div>
          </div>
        </div>
      )}
    </div>
  )
})

export default ReferralModal
